//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { RightsRequestCommunicationType } from '@apis/rights-management/rightsManagementApiModels';
import Badge, { BadgeType } from '@components/atoms/badge/Badge';
import { SectionText } from '@components/atoms/text/Text';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { ContentBadge } from '@uia/rights/badge/ContentBadge';
import { RightsRequestPathForm } from '@uia/rights/forms/RightsRequestPathForm';
import { isDefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

const RightsStatusText: Record<string, string> = {
  [RIGHTS_STATUS.QUEUED]: 'Queued',
  [RIGHTS_STATUS.PENDING]: 'Rights Pending',
  [RIGHTS_STATUS.INITIATED]: 'Rights Pending',
  [RIGHTS_STATUS.APPROVED]: 'Rights Approved',
  [RIGHTS_STATUS.EXPIRED]: 'Rights Rejected',
} as const;

export interface UIASupportedRightsRequestProps {
  approvalPhrase?: string;
  cuid: Cuid;
  requestType: RightsRequestOptions;
  rightsRequestText: string | undefined;
  setApprovalPhrase: (value?: string) => void;
  setRequestType: (selectedValue: RightsRequestOptions) => void;
  setRightsRequestText: (value?: string) => void;
}

export const UIASupportedRightsRequest = ({
  approvalPhrase,
  cuid,
  requestType,
  rightsRequestText,
  setApprovalPhrase,
  setRequestType,
  setRightsRequestText,
}: UIASupportedRightsRequestProps) => {
  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  if (isLoadingMetadata || isErrorMetadata) {
    return null;
  }

  const {
    rightsRequestStatus: { communicationType, status },
    socialDetails: {
      caption,
      networkUserImageUrl,
      networkUsername,
      postedTime,
    },
    socialNetwork,
  } = metadata;

  const statusText = RightsStatusText[status];

  const badgeType = (() => {
    switch (status) {
      case RIGHTS_STATUS.APPROVED:
        return BadgeType.Success;
      case RIGHTS_STATUS.EXPIRED:
        return BadgeType.Error;
      default:
        return BadgeType.Warning;
    }
  })();

  return (
    <Wrapper>
      <H3>
        <SectionText>Request Rights</SectionText>
        {statusText && (
          <StyledBadge size='small' text={statusText} type={badgeType} />
        )}
        {communicationType ===
          RightsRequestCommunicationType.BV_SAMPLING_CAMPAIGN && (
          <StyledBadge
            size='small'
            text='BV SAMPLING'
            type={BadgeType.Neutral}
          />
        )}
        {communicationType ===
          RightsRequestCommunicationType.BV_CREATORS_CAMPAIGN && (
          <StyledBadge
            size='small'
            text='BV CREATORS'
            type={BadgeType.Neutral}
          />
        )}
      </H3>
      {status !== RIGHTS_STATUS.APPROVED && (
        <div>Looks like you don't own the rights to this piece of media.</div>
      )}
      {isDefined(networkUsername) && (
        <ContentBadge
          avatarUrl={networkUserImageUrl}
          caption={caption}
          networkUsername={networkUsername}
          postedTime={postedTime}
          socialNetwork={socialNetwork ?? SocialNetwork.Unknown}
        />
      )}
      <RightsRequestPathForm
        approvalPhrase={approvalPhrase}
        cuid={cuid}
        requestType={requestType}
        rightsRequestText={rightsRequestText}
        setApprovalPhrase={setApprovalPhrase}
        setRequestType={setRequestType}
        setRightsRequestText={setRightsRequestText}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    width: 100%;
    padding: ${theme.spacings[3]};
  `,
);

const H3 = styled.h3(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    margin: ${theme.spacings[2]} ${theme.spacings[0]};

    span {
      display: flex;
      align-items: center;
    }
  `,
);

const StyledBadge = styled(Badge)`
  display: flex;
  align-items: center;
`;
